var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import BatchTraceDialog from 'components/Shared/BatchTraceDialog/BatchTraceDialog';
import { useIsIntersecting } from 'hooks';
import clamp from 'lodash/clamp';
import find from 'lodash/find';
import React, { createContext, memo, useContext, useEffect, useMemo, useReducer, useState, } from 'react';
import { PinTarget, } from 'types/types';
import BatchTracingReducer from './BatchTracingReducer';
import JourneyFilterReducer from './JourneyFilterReducer';
const ActiveContext = createContext(null);
const ActiveProvider = ({ lang, supplyChain, children, latestBatch, productId, isIntegration, trackSteps, }) => {
    const { isIntersecting, intersectionRef } = useIsIntersecting();
    /**
     * The initial selection state is used to determine if the user has selected a raw material or origin
     */
    const [initialRawMaterialSelection, setInitialRawMaterialSelection] = useState(true);
    const [initialOriginSelection, setInitialOriginSelection] = useState(true);
    const [batchTracingState, dispatchBatchTracingState] = useReducer(BatchTracingReducer, {
        steps: supplyChain.stepsSerialized || [],
        allRawMaterials: supplyChain.allRawMaterials,
        latestBatch,
        dialogOpen: false,
        isBatchTracingActive: false,
        filteredRawMaterials: supplyChain.allRawMaterials,
        mappedFilteredSteps: supplyChain.stepsSerialized,
        traceableChainStepActivitiesIds: [],
    });
    const [{ allStepActivities, filteredSteps, filteredStepActivities, filteredStepActivityLinks, allRawMaterials, selectedRawMaterialId, selectedStepIndex, selectedStepActivityId, }, dispatchJourneyFilterState,] = useReducer(JourneyFilterReducer, {
        // ### Initial values for the reducer state which are used to calculate the rest of the values
        steps: supplyChain.stepsSerialized || [],
        allRawMaterials: supplyChain.allRawMaterials || [],
        // ### Empty values that will be calculated in the reducer
        allStepActivities: [],
        allStepActivityLinks: [],
        filteredSteps: [],
        filteredStepActivities: [],
        filteredStepActivityLinks: [],
        selectedStepIndex: 0,
    });
    // If the site cluster overview is active in the stacking area, the extendSiteId will be the active site id
    const [activeSiteId, setActiveSiteId] = useState();
    const [didTrackInitial, setDidTrackInitial] = useState(false);
    const [activeImpactClaim, setActiveImpactClaim] = useState(null);
    const [animatedPin, setAnimatedPin] = useState({
        id: '',
        target: PinTarget.MAP,
    });
    /**
     * To show the raw material filter all activities have to contain an outputComponent with specified raw materials
     * And there have to be more than one unique raw material
     * Hint: If there are no activities, the filter is still active to prevent issues with rendering the initial raw material selection
     */
    const showRawMaterialFilter = useMemo(() => {
        return (!allStepActivities.length ||
            (allStepActivities.every(({ activity }) => { var _a; return !!((_a = activity.component) === null || _a === void 0 ? void 0 : _a.rawMaterials.length); }) &&
                new Set(allStepActivities
                    .flatMap(({ activity }) => { var _a; return (_a = activity.component) === null || _a === void 0 ? void 0 : _a.rawMaterials; })
                    .map(({ id }) => id)).size > 1));
    }, [allStepActivities]);
    const batchTracingPossible = useMemo(() => { var _a; return !!(latestBatch && latestBatch.date && ((_a = latestBatch.rawMaterials) === null || _a === void 0 ? void 0 : _a.length)); }, [latestBatch]);
    useEffect(() => {
        trackSteps === null || trackSteps === void 0 ? void 0 : trackSteps({
            stepIndex: selectedStepIndex + 1,
            totalSteps: filteredSteps.length,
        });
    }, [selectedStepIndex, filteredSteps]);
    useEffect(() => {
        if (didTrackInitial || !isIntersecting)
            return;
        trackSteps === null || trackSteps === void 0 ? void 0 : trackSteps({
            stepIndex: selectedStepIndex + 1,
            totalSteps: filteredSteps.length,
        });
        setDidTrackInitial(true);
    }, [isIntersecting]);
    useEffect(() => {
        var _a;
        if (batchTracingState.isBatchTracingActive) {
            // Check if the a traceable material is already selected and otherwise select the first traceable one
            const isTraceableRawMaterialsAlreadySelected = batchTracingState.filteredRawMaterials.findIndex(({ id }) => id === selectedRawMaterialId) > -1;
            // Initialize the state with the initial values and no applied filters
            dispatchJourneyFilterState({
                type: 'INITIALIZE',
                steps: batchTracingState.mappedFilteredSteps,
                allRawMaterials: batchTracingState.filteredRawMaterials,
                resetStepIndex: !isTraceableRawMaterialsAlreadySelected,
            });
            setInitialRawMaterialSelection(false);
            // Rerender the component with the selected raw material
            dispatchJourneyFilterState({
                type: 'SET_SELECTED_RAW_MATERIAL_ID',
                id: isTraceableRawMaterialsAlreadySelected
                    ? selectedRawMaterialId
                    : (_a = batchTracingState.filteredRawMaterials[0]) === null || _a === void 0 ? void 0 : _a.id,
                resetStepIndex: !isTraceableRawMaterialsAlreadySelected,
            });
        }
        else {
            dispatchJourneyFilterState({
                type: 'INITIALIZE',
                steps: batchTracingState.steps,
                allRawMaterials: batchTracingState.allRawMaterials,
                resetStepIndex: false,
            });
            // Rerender the component with the selected raw material
            dispatchJourneyFilterState({
                type: 'SET_SELECTED_RAW_MATERIAL_ID',
                id: selectedRawMaterialId,
                resetStepIndex: false, // We never need to reset the step index since we can be sure that the activity and step still exists after deactivating the batch tracing
            });
        }
    }, [batchTracingState.isBatchTracingActive]);
    const activeStep = useMemo(() => filteredSteps[selectedStepIndex], [filteredSteps, selectedStepIndex]);
    const activeStepActivity = useMemo(() => find(allStepActivities, ['id', selectedStepActivityId]), [selectedStepActivityId, allStepActivities]);
    const isSelectedActivityTraceable = useMemo(() => {
        var _a;
        return batchTracingState.isBatchTracingActive
            ? (_a = batchTracingState.traceableChainStepActivitiesIds) === null || _a === void 0 ? void 0 : _a.includes(activeStepActivity.id)
            : false;
    }, [
        activeStepActivity,
        batchTracingState.traceableChainStepActivitiesIds,
        batchTracingState.isBatchTracingActive,
    ]);
    const isSelectedStepTraceable = useMemo(() => {
        var _a;
        return batchTracingState.isBatchTracingActive
            ? (_a = activeStep === null || activeStep === void 0 ? void 0 : activeStep.chainStepActivities) === null || _a === void 0 ? void 0 : _a.some(({ id }) => { var _a; return (_a = batchTracingState.traceableChainStepActivitiesIds) === null || _a === void 0 ? void 0 : _a.includes(id); })
            : false;
    }, [
        activeStep,
        batchTracingState.traceableChainStepActivitiesIds,
        batchTracingState.isBatchTracingActive,
    ]);
    const allSelectedStepActivityRawMaterialIds = useMemo(() => {
        var _a, _b, _c;
        return (_c = (_b = (_a = activeStepActivity === null || activeStepActivity === void 0 ? void 0 : activeStepActivity.activity) === null || _a === void 0 ? void 0 : _a.component) === null || _b === void 0 ? void 0 : _b.rawMaterials) === null || _c === void 0 ? void 0 : _c.map(({ id }) => id);
    }, [activeStepActivity]);
    const hasSelectedStepActivityOtherIngredients = useMemo(() => !!selectedRawMaterialId &&
        (allSelectedStepActivityRawMaterialIds === null || allSelectedStepActivityRawMaterialIds === void 0 ? void 0 : allSelectedStepActivityRawMaterialIds.length) &&
        allSelectedStepActivityRawMaterialIds.some((id) => id !== selectedRawMaterialId), [allSelectedStepActivityRawMaterialIds, selectedRawMaterialId]);
    /**
     * Show the initial raw material selection when the raw material there are actually raw materials to select
     */
    useEffect(() => {
        if (initialRawMaterialSelection) {
            setInitialRawMaterialSelection(showRawMaterialFilter);
        }
    }, [showRawMaterialFilter]);
    /**
     * Show the initial origin selection when the active step has more than one activity
     */
    useEffect(() => {
        var _a;
        if (!initialRawMaterialSelection && initialOriginSelection) {
            setInitialOriginSelection(((_a = activeStep === null || activeStep === void 0 ? void 0 : activeStep.chainStepActivities) === null || _a === void 0 ? void 0 : _a.length) > 1);
        }
    }, [activeStep, initialRawMaterialSelection, initialOriginSelection]);
    const handleStepChange = (index) => {
        if (isNaN(index))
            return;
        const nextIndexSafe = clamp(index, 0, filteredSteps.length - 1);
        dispatchJourneyFilterState({
            type: 'SET_SELECTED_STEP_INDEX',
            index: nextIndexSafe,
        });
        trackSteps === null || trackSteps === void 0 ? void 0 : trackSteps({
            stepIndex: nextIndexSafe + 1,
            totalSteps: filteredSteps.length,
        });
    };
    const handleSelectedActivityChange = (id) => {
        setInitialRawMaterialSelection(false);
        setInitialOriginSelection(false);
        dispatchJourneyFilterState({
            type: 'SET_SELECTED_ACTIVITY_ID',
            id,
        });
    };
    const handleActivityIndexChange = (index) => {
        const { id } = allStepActivities[index];
        setInitialRawMaterialSelection(false);
        setInitialOriginSelection(false);
        dispatchJourneyFilterState({
            type: 'SET_SELECTED_ACTIVITY_ID',
            id,
        });
    };
    const handleSelectedRawMaterialChange = (id) => {
        setInitialRawMaterialSelection(false);
        dispatchJourneyFilterState({ type: 'SET_SELECTED_RAW_MATERIAL_ID', id });
    };
    const handleGoToBatchDataStep = () => {
        if (!batchTracingState.isBatchTracingActive)
            return;
        const firstStepWithBatchData = filteredSteps.findIndex((step) => step.chainStepActivities.some((activity) => activity.batchDetails));
        handleStepChange(firstStepWithBatchData);
    };
    const handleTraceMyBatchClick = () => {
        dispatchBatchTracingState({ type: 'OPEN_DIALOG' });
    };
    const resetBatchTraceDialog = () => {
        dispatchBatchTracingState({ type: 'CLOSE_DIALOG' });
    };
    const handleBatchTraceSubmit = (batchData) => __awaiter(void 0, void 0, void 0, function* () {
        dispatchBatchTracingState({
            type: 'SET_BATCH_TRACING_ACTIVE',
            batchData,
        });
    });
    const handleStopBatchTracingClick = () => {
        dispatchBatchTracingState({
            type: 'SET_BATCH_TRACING_INACTIVE',
        });
    };
    const state = {
        activeSiteId,
        initialRawMaterialSelection,
        initialOriginSelection,
        batchTracingPossible,
        showRawMaterialFilter,
        isBatchTracingActive: batchTracingState.isBatchTracingActive,
        // The values take the filtered items that were created in the useMemo
        steps: filteredSteps,
        allStepActivities: filteredStepActivities,
        allStepActivityLinks: filteredStepActivityLinks,
        activeStepActivityId: activeStepActivity === null || activeStepActivity === void 0 ? void 0 : activeStepActivity.id,
        activeStepActivity,
        activeStepIndex: selectedStepIndex,
        activeStep,
        animatedPin,
        activeImpactClaim,
        allRawMaterials,
        selectedRawMaterialId,
        summary: {
            countryCount: supplyChain.countryCount,
            originCount: supplyChain.originCount,
            farmCount: supplyChain.farmCount,
        },
        isIntegration,
        allSelectedStepActivityRawMaterialIds,
        hasSelectedStepActivityOtherIngredients,
        isSelectedActivityTraceable,
        isSelectedStepTraceable,
        handleStepChange,
        handleSelectedActivityChange,
        handleNodeChangeIndexed: handleActivityIndexChange,
        handleAnimatedPinChange: setAnimatedPin,
        setActiveImpactClaim,
        handleSelectedRawMaterialChange,
        handleTraceMyBatchClick,
        onActiveSiteChange: setActiveSiteId,
        handleStopBatchTracingClick,
        handleGoToBatchDataStep,
    };
    return (React.createElement(ActiveContext.Provider, { value: state },
        batchTracingPossible && (React.createElement(BatchTraceDialog, { open: batchTracingState.dialogOpen, productId: productId, lang: lang, latestBatch: batchTracingState.latestBatch, onSubmit: handleBatchTraceSubmit, onClose: resetBatchTraceDialog })),
        React.createElement("div", { style: { width: '100%', height: '100%' }, ref: intersectionRef }, children)));
};
export const useActiveContext = () => useContext(ActiveContext);
export default memo(ActiveProvider);
